import { IContentExtract } from "@/core/shared/api/types";
import { filterStateSchema, IGetFiltersByGroupResult } from "@/core/utils/legend-state/feature/filter";
import { IPeersetCompany } from "@/features/report-builder/state/types";
import { z } from "zod";

const config = z.object({
  company: z.object({
    measure_id_to_measure_name_map: z.record(z.string()),
    filters: z.object({
      hide: z.boolean(),
    }),
  }),
  measure: z.object({
    hide: z.boolean(),
    default: z.string(),
    measures: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        unit: z.string().optional(),
        acronym: z.string().optional(),
        showSign: z.boolean().optional(),
        domain: z.tuple([z.number(), z.number()]).optional(),
        compactYAxisValues: z.boolean().optional(),
        filter: z.object({
          checked: z.boolean(),
          disabled: z.boolean().optional(),
        }),
      }),
    ),
  }),
  sentiment: z.object({
    hide: z.boolean(),
    sentiments: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        filter: z.object({
          id: z.string().optional(),
          checked: z.boolean(),
          disabled: z.boolean(),
          label: z.string().optional(),
        }),
      }),
    ),
  }),
});

const axis_schema = z.object({
  data_key: z.string().optional(),
  label: z.string().optional(),
  compactValues: z.boolean().optional(),
  domain: z.tuple([z.number(), z.number()]).optional(),
  unit: z.string().optional(),
});

export const local_schema = z.object({
  label: z.string(),
  ui_merged: z.boolean(),
  config,
  views: z.object({
    currentView: z.union([z.literal("analysis"), z.literal("alerts")]),
    analysis: z.object({
      filters: z.object({
        company: filterStateSchema,
        topic: filterStateSchema,
        timeframe: filterStateSchema,
        sentiment: filterStateSchema,
        applied_filters: z.object({
          company_id: z.array(z.string()).optional(),
          topic_name: z.array(z.string()).optional(),
          timeframe: z.array(z.string()).optional(),
          sentiment: z.array(z.string()).optional(),
        }),
      }),
    }),
  }),
});

export type ILocalState$ = z.infer<typeof local_schema>;

export type IRemoteState$ = {
  evidence: {
    pending: boolean;
    error: boolean;
    data: Record<string, Record<string, Record<string, ITransformedContentExtract[]>>>;
  };
};

export interface ICreateStateProps {
  local?: object;
}

export interface IActions {
  views: {
    analysis: {
      filters: {
        company: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
        sentiment: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
        topic: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
        timeframe: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          toggleByRange: (instance_id: string, range: `${number}-${number}`) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
      };
    };
  };
}

export interface IObservers {
  debug: (instance_id: string) => void;
  view: {
    analysis: {
      filters: {
        sentiment: {
          init: (instance_id: string) => void;
        };
        company: {
          init: (instance_id: string) => void;
        };
        topic: {
          init: (instance_id: string) => void;
        };
        timeframe: {
          init: (instance_id: string) => void;
        };
      };
    };
  };
}

export interface ITransformedContentExtract extends IContentExtract {
  company: IPeersetCompany | null;
}
